/**
 * What a staff member may open. One list, shared by the API (which enforces it on every request) and the web UI (which hides what
 * is not allowed). `owner` and `admin` always have everything; for everybody else the stored list wins, and a staff member
 * with no stored list gets the default of their role.
 */
export const PERMISSIONS = ["pos", "loyalty", "customers", "whatsapp", "parties", "wallet", "admin", "reports", "expenses", "receipts", "shifts", "einvoice", "audit", "pricing", "pricing_edit", "hardware", "downloads", "staff", "setup"] as const;
export type Permission = (typeof PERMISSIONS)[number];
export type AccessRole = "cashier" | "manager" | "admin" | "owner";

/** Permission -> a short key into the translations plus whether it is a sensitive one (the UI warns). */
export const PERMISSION_INFO: Record<Permission, { group: "daily" | "business" | "control"; sensitive: boolean }> = {
  pos: { group: "daily", sensitive: false },
  loyalty: { group: "daily", sensitive: false },
  customers: { group: "daily", sensitive: true },
  whatsapp: { group: "daily", sensitive: false },
  parties: { group: "daily", sensitive: false },
  wallet: { group: "daily", sensitive: false },
  admin: { group: "business", sensitive: true },
  reports: { group: "business", sensitive: true },
  expenses: { group: "business", sensitive: true },
  receipts: { group: "business", sensitive: true },
  shifts: { group: "business", sensitive: true },
  einvoice: { group: "control", sensitive: true },
  audit: { group: "control", sensitive: true },
  pricing: { group: "business", sensitive: false },
  pricing_edit: { group: "business", sensitive: true },
  hardware: { group: "business", sensitive: false },
  downloads: { group: "business", sensitive: false },
  staff: { group: "control", sensitive: true },
  setup: { group: "control", sensitive: true },
};

export const ROLE_DEFAULTS: Record<AccessRole, readonly Permission[]> = {
  cashier: ["pos", "loyalty", "whatsapp", "parties", "wallet", "hardware"],
  manager: ["pos", "loyalty", "customers", "whatsapp", "parties", "wallet", "hardware", "admin", "reports", "expenses", "receipts", "shifts", "pricing", "downloads"],
  admin: PERMISSIONS,
  owner: PERMISSIONS,
};

/** Ready-made sets offered by the staff screen; "custom" is whatever the checkboxes say. */
export const PERMISSION_PRESETS = {
  pos_only: ["pos"],
  front_desk: ["pos", "loyalty", "customers", "whatsapp", "parties", "wallet"],
  supervisor: ROLE_DEFAULTS.manager,
  back_office: ["admin", "reports", "expenses", "receipts", "shifts", "pricing", "downloads", "hardware"],
} as const satisfies Record<string, readonly Permission[]>;

export const isPermission = (p: unknown): p is Permission => typeof p === "string" && (PERMISSIONS as readonly string[]).includes(p);

export function effectivePermissions(role: AccessRole, stored: readonly string[] | null | undefined): Permission[] {
  if (role === "owner" || role === "admin") return [...PERMISSIONS];
  const list = stored ? stored.filter(isPermission) : [...ROLE_DEFAULTS[role]];
  return PERMISSIONS.filter((p) => list.includes(p));
}

const RANK: Record<AccessRole, number> = { cashier: 1, manager: 2, admin: 3, owner: 4 };
export const roleRank = (r: AccessRole) => RANK[r];

/** A cashier who was given a back-office page also needs the manager-level calls that page makes. */
const LIFTS: readonly Permission[] = ["admin", "reports", "expenses", "receipts", "shifts", "audit", "pricing", "downloads", "setup", "staff"];
export function effectiveRole(role: AccessRole, permissions: readonly Permission[]): AccessRole {
  const lifted: AccessRole = "manager";
  return RANK[role] < RANK.manager && permissions.some((p) => LIFTS.includes(p)) ? lifted : role;
}
